import { UserDeliveriesResponseSchema, type UserDeliveriesResponse } from '../schemas/deliveries';
// Delivery ids are UUIDs aligned to the detail mock's keys (deliveryDetailMockIds) so a
// list-row click opens the matching «Entrega» detail; the overflow user's extra rows use
// fresh UUIDs (the detail mock falls back to a bare detail for those) — except its second row,
// which points at the AUTOCONFIRMED detail fixture so «Autoconfirmado» is reachable
// by clicking rather than by typing a URL (CBX-11159).
import { deliveryDetailMockIds, isoDaysFromNow } from './deliveryDetail';

// Fixtures in the snake_case wire shape, parsed through the schema so the mock can
// never drift from the contract. Statuses carry the canonical lowercase wire strings
// the backend confirmed (underscores: non_collected). Keyed by USER ID, pre-sorted newest first, and
// covering the cases the card must handle: a shared delivery (👥), a partial row
// (no tracking/carrier → "—"), a delivery not yet in a box (box_number null → "—"),
// a non-Madrid locker timezone, an empty user, an overflow user (>5, the count
// exceeds the list) and a transient failure. Addresses span SEVERAL buildings per «Usuario» —
// the case the clipboard column exists for — with one row whose building has none on file, and
// each one matches its row's locker timezone. Building addresses, so never a «Matrícula» suffix:
// that belongs to the portal strings of the address search, not to a delivery row.
const RAW_DATASET: Record<string, unknown> = {
  '330148': {
    count: 5,
    deliveries: [
      {
        id: deliveryDetailMockIds.stored,
        // Relative, echoing the detail fixture — see isoDaysFromNow.
        stored_date: isoDaysFromNow(-3),
        collected_date: null,
        status: 'stored',
        box_number: 'DIZ03',
        tracking: '1Z2X4A9912',
        carrier: 'Amazon Logistics',
        shared_with_recipient: false,
        locker_timezone: 'Europe/Madrid',
        location: { address: 'Calle Bolivia 39, 28020 Madrid' },
      },
      {
        id: deliveryDetailMockIds.collected,
        stored_date: '2026-05-12T09:05:00Z',
        collected_date: '2026-05-14T17:20:00Z',
        status: 'collected',
        box_number: 'DIZ12',
        tracking: '1Z7B5510023',
        carrier: 'SEUR',
        shared_with_recipient: false,
        locker_timezone: 'Europe/Madrid',
        location: { address: 'Calle Bolivia 39, 28020 Madrid' },
      },
      {
        id: deliveryDetailMockIds.expired,
        stored_date: '2026-05-04T20:45:00Z',
        collected_date: null,
        status: 'expired',
        box_number: 'DIZ05',
        tracking: '1Z5C7781190',
        carrier: 'GLS',
        shared_with_recipient: true,
        locker_timezone: 'Europe/Madrid',
        // A shared (👥) row: the building is the titular's, not this «Usuario»'s.
        location: { address: 'Avenida de Brasil 17, 28020 Madrid' },
      },
      {
        id: deliveryDetailMockIds.retrieved,
        stored_date: '2026-04-28T11:10:00Z',
        collected_date: '2026-05-06T08:40:00Z',
        status: 'retrieved',
        box_number: 'LDN19',
        tracking: '1Z2155540077',
        carrier: 'DHL',
        shared_with_recipient: false,
        locker_timezone: 'Europe/London',
        location: { address: '221B Baker Street, London NW1 6XE' },
      },
      {
        id: deliveryDetailMockIds.nonCollected,
        stored_date: '2026-04-19T07:55:00Z',
        collected_date: null,
        status: 'non_collected',
        box_number: 'DIZ07',
        shared_with_recipient: false,
        locker_timezone: 'Europe/Madrid',
        // No address row on file for this building — the column pastes «—».
        location: { address: null },
      },
    ],
  },
  // No deliveries in the last 12 months.
  '330149': { count: 0, deliveries: [] },
  // Overflow: the count exceeds the five rows the card shows.
  '330151': {
    count: 12,
    deliveries: [
      {
        id: deliveryDetailMockIds.storedExtended,
        stored_date: '2026-06-20T10:00:00Z',
        collected_date: null,
        status: 'stored',
        box_number: null, // stored but not yet in a box → the row renders "—"
        tracking: 'TRK0001',
        carrier: 'Amazon Logistics',
        shared_with_recipient: false,
        locker_timezone: 'Europe/Madrid',
        location: { address: 'Gran Vía 28, 28013 Madrid' },
      },
      {
        id: deliveryDetailMockIds.collectedAutoconfirmed,
        stored_date: '2026-06-18T10:00:00Z',
        collected_date: '2026-06-19T10:00:00Z',
        status: 'collected',
        box_number: 'ABC02',
        tracking: 'TRK0002',
        carrier: 'SEUR',
        shared_with_recipient: false,
        locker_timezone: 'Europe/Madrid',
        location: { address: 'Gran Vía 28, 28013 Madrid' },
      },
      {
        id: 'e0000000-0000-4000-8000-000000000003',
        stored_date: '2026-06-15T10:00:00Z',
        collected_date: '2026-06-16T10:00:00Z',
        status: 'collected',
        box_number: 'ABC03',
        tracking: 'TRK0003',
        carrier: 'GLS',
        shared_with_recipient: true,
        locker_timezone: 'Europe/Madrid',
        location: { address: 'Calle Serrano 41, 28001 Madrid' },
      },
      {
        id: 'e0000000-0000-4000-8000-000000000004',
        stored_date: '2026-06-10T10:00:00Z',
        collected_date: '2026-06-12T10:00:00Z',
        status: 'collected',
        box_number: 'ABC04',
        tracking: 'TRK0004',
        carrier: 'DHL',
        shared_with_recipient: false,
        locker_timezone: 'Europe/Madrid',
        location: { address: 'Gran Vía 28, 28013 Madrid' },
      },
      {
        id: 'e0000000-0000-4000-8000-000000000005',
        stored_date: '2026-06-05T10:00:00Z',
        collected_date: '2026-06-07T10:00:00Z',
        status: 'collected',
        box_number: 'ABC05',
        tracking: 'TRK0005',
        carrier: 'Correos',
        shared_with_recipient: false,
        locker_timezone: 'Europe/Madrid',
        location: { address: 'Gran Vía 28, 28013 Madrid' },
      },
    ],
  },
};

const ERROR_IDS = new Set(['330150']);

export async function fetchUserDeliveriesMock(id: string): Promise<UserDeliveriesResponse> {
  // Simulate network latency for a realistic dev experience.
  await new Promise((resolve) => setTimeout(resolve, 100));
  if (ERROR_IDS.has(id)) {
    throw new Error('deliveries unavailable');
  }
  return UserDeliveriesResponseSchema.parse(RAW_DATASET[id] ?? { count: 0, deliveries: [] });
}
